import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { MapPin, Search, Loader2 } from "lucide-react";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Search is biased to Basingstoke, Hampshire.
const BIAS_CENTER = { lat: 51.2667, lng: -1.0876 };
const BIAS_RADIUS_M = 30000;

let placesLibPromise = null;
function loadPlacesLib() {
  if (placesLibPromise) return placesLibPromise;
  if (!MAPS_KEY || MAPS_KEY === "YOUR_GOOGLE_MAPS_API_KEY") {
    placesLibPromise = Promise.reject(new Error("No Maps API key"));
    return placesLibPromise;
  }
  setOptions({ key: MAPS_KEY, v: "weekly" });
  placesLibPromise = Promise.all([
    importLibrary("places"),
    importLibrary("core"),
    importLibrary("geocoding"),
  ]).then(() => window.google.maps);
  return placesLibPromise;
}

/**
 * Reverse-geocode a lat/lng to an address string.
 * Returns { name, lat, lng } or a coordinate fallback on failure.
 * (Geocoder is current — not part of the deprecated Places legacy API.)
 */
export async function reverseGeocode(lat, lng) {
  try {
    const maps = await loadPlacesLib();
    const geocoder = new maps.Geocoder();
    const { results } = await geocoder.geocode({ location: { lat, lng } });
    if (results && results.length > 0) {
      return { name: results[0].formatted_address, lat, lng };
    }
  } catch (e) { /* ignore */ }
  return { name: `${lat.toFixed(5)}, ${lng.toFixed(5)}`, lat, lng };
}

/**
 * Forward-geocode an address string → { lat, lng, formatted } (UK-biased),
 * or null if it can't be resolved. Used for the AI Booking fare estimate;
 * the final booking still requires explicit address confirmation.
 */
export async function forwardGeocode(query) {
  if (!query || !query.trim()) return null;
  try {
    const maps = await loadPlacesLib();
    const geocoder = new maps.Geocoder();
    const { results } = await geocoder.geocode({
      address: query.trim(),
      componentRestrictions: { country: "gb" },
    });
    if (results && results.length > 0) {
      const loc = results[0].geometry.location;
      return { lat: loc.lat(), lng: loc.lng(), formatted: results[0].formatted_address };
    }
  } catch (e) { /* ignore */ }
  return null;
}

/**
 * PlaceAutocomplete - input with Google Places autocomplete suggestions.
 *
 * Migrated off the deprecated legacy Places API
 * (AutocompleteService.getPlacePredictions + PlacesService.getDetails) to
 * the current Places API:
 *   - AutocompleteSuggestion.fetchAutocompleteSuggestions() for predictions
 *   - placePrediction.toPlace() + place.fetchFields() for details
 * Session tokens are still used so a search→select counts as one billable
 * Autocomplete session.
 *
 * Falls back gracefully (preset-only) if the Places API is unavailable.
 *
 * Props:
 *  - value: { name, lat, lng }
 *  - onChange: (newValue) => void
 *  - placeholder
 *  - testId
 *  - icon
 */
export const PlaceAutocomplete = ({ value, onChange, onTextChange, placeholder, testId, icon, seedSearch, requireSelection }) => {
  const inputRef = useRef(null);
  const skipSyncRef = useRef(false);
  const wrapRef = useRef(null);
  const [query, setQuery] = useState(value?.name || "");
  const [suggestions, setSuggestions] = useState([]); // AutocompleteSuggestion[]
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(true);
  const [pos, setPos] = useState(null); // portal anchor {left, top, width}
  const placesRef = useRef(null); // google.maps.places namespace
  const sessionToken = useRef(null);
  const debounceRef = useRef(null);

  // Keep input synced when parent updates value (e.g., preset click)
  useEffect(() => {
    if (skipSyncRef.current) { skipSyncRef.current = false; return; }
    setQuery(value?.name || "");
  }, [value?.name]);

  // Anchor the portaled dropdown directly under this input, and keep it
  // pinned on scroll/resize. Rendering in a body portal (below) sidesteps
  // parent stacking-context / overflow-clipping so stacked instances (pickup +
  // dropoff) and modal usages never overlap or hide each other's suggestions.
  useEffect(() => {
    if (!open || suggestions.length === 0) return;
    const updatePos = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({ left: r.left, top: r.bottom, width: r.width });
    };
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open, suggestions.length]);

  useEffect(() => {
    loadPlacesLib()
      .then((maps) => {
        placesRef.current = maps.places;
        sessionToken.current = new maps.places.AutocompleteSessionToken();
      })
      .catch(() => setAvailable(false));
  }, []);

  const fetchSuggestions = async (input) => {
    const places = placesRef.current;
    if (!places || !input || input.length < 2) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const { suggestions: results } =
        await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken: sessionToken.current,
          includedRegionCodes: ["gb"],
          locationBias: { center: BIAS_CENTER, radius: BIAS_RADIUS_M },
        });
      const preds = (results || []).filter((s) => s.placePrediction).slice(0, 5);
      setSuggestions(preds);
    } catch (e) {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const onQueryChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    setOpen(true);
    // Editing the text invalidates a previously saved location (keeps typed text).
    if (requireSelection && value) {
      skipSyncRef.current = true;
      onChange(null);
    }
    // Report raw typed text so a parent can enable a text-based fallback
    // (e.g. server-side geocoding) even if no suggestion is clicked.
    onTextChange?.(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(q), 250);
  };

  // Seed-search: when pre-filled with address TEXT but no confirmed location
  // (lat is null — e.g. an AI Booking draft), auto-fetch suggestions and open
  // the dropdown so the operator can confirm the precise address in one click.
  useEffect(() => {
    if (!seedSearch || !value?.name || value?.lat != null) return;
    let cancelled = false;
    loadPlacesLib()
      .then((maps) => {
        if (cancelled) return;
        placesRef.current = maps.places;
        if (!sessionToken.current) sessionToken.current = new maps.places.AutocompleteSessionToken();
        fetchSuggestions(value.name);
        setOpen(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedSearch, value?.name]);

  const selectSuggestion = async (suggestion) => {
    const pred = suggestion?.placePrediction;
    if (!pred) return;
    try {
      const place = pred.toPlace();
      await place.fetchFields({
        fields: ["displayName", "formattedAddress", "location"],
      });
      const loc = place.location;
      if (!loc) return;
      const name =
        place.formattedAddress || place.displayName || pred.text?.text || "";
      onChange({
        name,
        lat: typeof loc.lat === "function" ? loc.lat() : loc.lat,
        lng: typeof loc.lng === "function" ? loc.lng() : loc.lng,
      });
      setQuery(name);
      setOpen(false);
      setSuggestions([]);
      // New session token after a selection (ends the billable session).
      sessionToken.current = new placesRef.current.AutocompleteSessionToken();
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="relative">
      <div ref={wrapRef} className="flex items-center px-3 py-2 border-b border-border bg-white">
        {icon || <MapPin className="w-4 h-4 text-muted-foreground mr-2" />}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={onQueryChange}
          onFocus={() => query.length >= 2 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={available ? placeholder || "Search address…" : "Pick a preset below"}
          className="flex-1 outline-none bg-transparent text-sm"
          data-testid={testId}
          autoComplete="off"
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </div>
      {requireSelection && !value && query.trim() && (
        <p className="px-3 py-1.5 text-xs text-[#B45309] bg-[#FFFBEB]" data-testid={`${testId}-select-hint`}>
          Tap an address from the suggestions list to confirm it
        </p>
      )}

      {open && suggestions.length > 0 && pos && createPortal(
        <div
          style={{ position: "fixed", left: pos.left, top: pos.top, width: pos.width, zIndex: 10000 }}
          className="bg-white border border-border shadow-xl max-h-72 overflow-y-auto"
          data-testid={`${testId}-suggestions`}
        >
          {suggestions.map((s) => {
            const pred = s.placePrediction;
            const mainText = pred.mainText?.text || pred.text?.text || "";
            const secondaryText = pred.secondaryText?.text || "";
            return (
              <button
                key={pred.placeId}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
                className="w-full text-left px-3 py-2.5 hover:bg-secondary transition-colors border-b border-border last:border-b-0 flex items-start gap-2"
                data-testid={`suggestion-${pred.placeId}`}
              >
                <Search className="w-3.5 h-3.5 text-muted-foreground mt-1 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{mainText}</div>
                  {secondaryText && (
                    <div className="text-xs text-muted-foreground truncate">{secondaryText}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

export default PlaceAutocomplete;
